import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import { css } from '@xstyled/styled-components';
import { th } from '@xstyled/system';
import React from 'react';
import _extends from '@babel/runtime/helpers/esm/extends';

var _templateObject;
var cardStyles = function cardStyles() {
  return css(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n  ", ";\n  border-radius: md;\n  border-style: solid;\n  border-width: sm;\n  box-shadow: sm;\n"])), th('cards.default'));
};

/**
 * Clamps the given number between min and max values. Returns value if within
 * range, or closest bound.
 */
var clamp = function clamp(val, min, max) {
  if (val == null) {
    return val;
  }
  if (max < min) {
    throw new Error('Max must be greater than min');
  }
  return Math.min(Math.max(val, min), max);
};

var VARIANTS = {
  error: 'colors.danger.500',
  focused: 'colors.primary.500',
  info: 'colors.info.500',
  success: 'colors.success.500',
  warning: 'colors.warning.500'
};
var getVariantColor = function getVariantColor(variant) {
  var key = VARIANTS[variant];
  return key ? th(key) : null;
};

var _templateObject$1;
var fieldStyles = function fieldStyles(_ref) {
  var size = _ref.size,
    type = _ref.type,
    variant = _ref.variant;
  return css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteralLoose(["\n  ", ";\n  width: 100%;\n  border-color: ", ";\n  transition: medium;\n  appearance: none;\n  ", ";\n\n  &::placeholder {\n    ", ";\n  }\n\n  &:focus {\n    ", ";\n  }\n\n  &[disabled] {\n    ", ";\n  }\n\n  &[aria-checked='true'] {\n    ", ";\n  }\n\n  &:invalid,\n  &:-moz-submit-invalid,\n  &:-moz-ui-invalid {\n    box-shadow: none;\n  }\n"])), th('fields.default'), getVariantColor(variant), size && th("fields.sizes." + size), th('fields.placeholder'), th('fields.focused'), th('fields.disabled'), ['radio', 'checkbox'].includes(type) && th("fields." + type + ".checked"));
};

var flattenStrings = function flattenStrings(array) {
  return array.reduce(function (flattenedArray, value) {
    var lastIndex = flattenedArray.length - 1;
    var last = flattenedArray[lastIndex];
    if (typeof last === 'string' && typeof value === 'string') {
      flattenedArray[lastIndex] = last + value;
    } else {
      flattenedArray.push(value);
    }
    return flattenedArray;
  }, []);
};

var getMax = function getMax(width, height) {
  if (height === void 0) {
    height = width;
  }
  var widthValue = parseInt(width, 10);
  var heightValue = parseInt(height, 10);
  var diff = widthValue - heightValue;
  return diff >= 0 ? width : height;
};

var getHexValueAtLocation = function getHexValueAtLocation(value, start, count) {
  return parseInt(value.substring(start, count), 16);
};
var isValidHex = function isValidHex(hex) {
  return /(^[0-9a-fA-F]{6}$)|(^[0-9a-fA-F]{3}$)/.test(hex);
};
var hexToRGB = function hexToRGB(hex) {
  if (!hex) {
    return;
  }
  hex = hex.replace('#', '');
  if (!isValidHex(hex)) {
    return;
  }
  if (hex.length === 3) {
    hex = "" + hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  var hexToR = function hexToR(hex) {
    return getHexValueAtLocation(hex, 0, 2);
  };
  var hexToG = function hexToG(hex) {
    return getHexValueAtLocation(hex, 2, 4);
  };
  var hexToB = function hexToB(hex) {
    return getHexValueAtLocation(hex, 4, 6);
  };
  return hexToR(hex) + ", " + hexToG(hex) + ", " + hexToB(hex);
};

var hexToRGBA = function hexToRGBA(hex, transparency) {
  if (transparency === void 0) {
    transparency = 1;
  }
  if (!hex) return;
  var toRgb = hexToRGB(hex);
  return "rgba(" + toRgb + ", " + transparency + ")";
};

var _templateObject$2;
var centerContent = /*#__PURE__*/css(_templateObject$2 || (_templateObject$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n"])));

var _templateObject$3;
var overflowEllipsis = /*#__PURE__*/css(_templateObject$3 || (_templateObject$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n"])));

var wrapChildren = function wrapChildren(children) {
  return React.Children.toArray(children).map(function (child) {
    return ['number', 'string'].includes(typeof child) ? /*#__PURE__*/React.createElement("span", {
      key: child
    }, child) : child;
  });
};

var createEvent = function createEvent(props) {
  return {
    preventDefault: function preventDefault() {},
    target: _extends({}, props)
  };
};

/* eslint-disable no-console */

var DEV = process.env.NODE_ENV !== 'production';
function cascade(value, arg) {
  if (typeof value === 'function') {
    return cascade(value(arg), arg);
  }
  return value;
}
var get = function get(from, path) {
  var paths = String(path).split('.');
  var pathsLength = paths.length;
  var result = from;
  for (var i = 0; i < pathsLength; i += 1) {
    if (result === undefined) return result;
    var _path = paths[i];
    result = is(result[_path]) ? result[_path] : undefined;
  }
  return result;
};
var is = function is(n) {
  return n !== undefined && n !== null;
};
var getThemeValue = function getThemeValue(props, path, initial) {
  if (initial === void 0) {
    initial = props.theme;
  }
  return cascade(get(initial, path), props);
};
var warn = function warn(condition, message) {
  if (DEV) {
    if (!condition && console.error) {
      console.error(message);
    }
  }
};
var isPristine = function isPristine(connected, touched) {
  return connected && !touched;
};
var getVariant = function getVariant(_ref) {
  var connected = _ref.connected,
    error = _ref.error,
    touched = _ref.touched,
    warning = _ref.warning;
  if (isPristine(connected, touched)) {
    return undefined;
  }
  if (error) {
    return 'error';
  }
  if (warning) {
    return 'warning';
  }
  return undefined;
};

export { VARIANTS, cardStyles, cascade, centerContent, clamp, createEvent, fieldStyles, flattenStrings, get, getMax, getThemeValue, getVariant, getVariantColor, hexToRGB, hexToRGBA, is, overflowEllipsis, warn, wrapChildren };
